import { useRouteError } from "react-router";
import { ConvexError } from "convex/values";

import { Button, Carte, EnTeteDePanneau } from "@/shared/ui";
import { useSession } from "@/core/auth";
import { ENTETE_ECRAN } from "@/core/auth/enteteEcran";

const AUTH_ERROR_CODES = new Set([
  "UNAUTHENTICATED",
  "USER_NOT_SYNCED",
  "NOT_A_MEMBER",
]);

function extractConvexErrorCode(error: unknown): string | null {
  if (error instanceof ConvexError) {
    const data = error.data;
    if (data && typeof data === "object" && "code" in data) {
      const code = (data as { code: unknown }).code;
      return typeof code === "string" ? code : null;
    }
    return null;
  }
  // Fallback : parser le message (les ConvexError remontent souvent stringifiées
  // à travers le pipeline Suspense/Router et perdent leur instanceof).
  if (error instanceof Error) {
    const m = error.message.match(/\{"code":"([A-Z_]+)"\}/);
    return m?.[1] ?? null;
  }
  return null;
}

export function RouterErrorBoundary() {
  const error = useRouteError();
  const { signOut } = useSession();
  const code = extractConvexErrorCode(error);

  if (code && AUTH_ERROR_CODES.has(code)) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-canvas p-4 text-fg">
        <Carte remplissage="lg" className="max-w-md sm:p-10">
          <div className="flex flex-col items-center gap-6 text-center">
            <EnTeteDePanneau
              titre="Session expirée"
              sousTitre={
                code === "UNAUTHENTICATED"
                  ? "Ta session WorkOS n'est plus reconnue. Reconnecte-toi pour récupérer ton bureau."
                  : code === "USER_NOT_SYNCED"
                    ? "Ton compte n'est pas synchronisé. Reconnecte-toi pour terminer la synchro."
                    : "Tu n'es plus membre de cet espace. Reconnecte-toi et choisis un autre espace."
              }
              className={ENTETE_ECRAN}
            />
            <Button variant="accent" size="xl" onClick={() => void signOut()} className="w-full">
              Se reconnecter
            </Button>
          </div>
        </Carte>
      </div>
    );
  }

  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "Erreur inconnue";

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-canvas p-4 text-fg">
      <Carte remplissage="lg" className="max-w-lg sm:p-10">
        <div className="flex flex-col gap-5">
          <EnTeteDePanneau titre="Erreur inattendue" className={ENTETE_ECRAN} />
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[12px] text-muted">
            {message}
          </pre>
          <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-center">
            <Button variant="primary" size="xl" onClick={() => window.location.reload()} className="w-full sm:w-auto">
              Recharger
            </Button>
            <Button variant="secondary" size="xl" onClick={() => void signOut()} className="w-full sm:w-auto">
              Se reconnecter
            </Button>
          </div>
        </div>
      </Carte>
    </div>
  );
}
